import { Link, useSearchParams } from 'react-router-dom';
import { useMemo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { blogPosts } from './data';
import SEO, { breadcrumbJsonLd } from '../../components/SEO';

const POSTS_PER_PAGE = 9;

/** Newest first so recently added posts are not buried after older arrays in `data.ts`. */
function postsNewestFirst() {
  return [...blogPosts].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

/** Compact locale-aware date for cards. */
function formatDate(date: string, locale: string): string {
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return date;
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Category pill colors aligned with blog content types (customer, comparisons, delivery ops, etc.). */
function categoryBadgeClass(category: string): string {
  const c = category.toLowerCase();
  if (c.includes('customer') || c.includes('experience') || c.includes('cx'))
    return 'bg-rose-50 text-rose-700 border border-rose-100';
  if (
    c.includes('compar') ||
    c.includes('alternative') ||
    c.includes('vs') ||
    c.includes('route planner')
  )
    return 'bg-violet-50 text-violet-700 border border-violet-100';
  if (
    c.includes('delivery') ||
    c.includes('last mile') ||
    c.includes('dispatch') ||
    c.includes('fleet')
  )
    return 'bg-sky-50 text-sky-700 border border-sky-100';
  if (c.includes('route') || c.includes('optimization'))
    return 'bg-purple-50 text-purple-700 border border-purple-100';
  if (c.includes('integration') || c.includes('ai') || c.includes('automation'))
    return 'bg-emerald-50 text-emerald-700 border border-emerald-100';
  return 'bg-blue-50 text-blue-700 border border-blue-100';
}

export default function BlogPage() {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const sortedPosts = useMemo(() => postsNewestFirst(), []);

  const uniqueCategories = useMemo(
    () =>
      Array.from(new Set(sortedPosts.map((p) => p.category))).sort((a, b) =>
        a.localeCompare(b)
      ),
    [sortedPosts]
  );

  const filteredPosts = useMemo(() => {
    let list = sortedPosts;
    if (selectedCategory !== 'all') {
      list = list.filter((p) => p.category === selectedCategory);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((p) => p.title.toLowerCase().includes(q));
    }
    return list;
  }, [sortedPosts, selectedCategory, searchQuery]);

  const totalPages = Math.max(1, Math.ceil(filteredPosts.length / POSTS_PER_PAGE));
  const pageFromUrl = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const currentPage = Math.min(pageFromUrl, totalPages);

  const paginatedPosts = useMemo(() => {
    const start = (currentPage - 1) * POSTS_PER_PAGE;
    return filteredPosts.slice(start, start + POSTS_PER_PAGE);
  }, [filteredPosts, currentPage]);

  /** Show a featured hero only on the default, unfiltered first page. */
  const isDefaultView =
    selectedCategory === 'all' && searchQuery.trim() === '' && currentPage === 1;
  const featuredPost = isDefaultView ? paginatedPosts[0] : undefined;
  const gridPosts = featuredPost ? paginatedPosts.slice(1) : paginatedPosts;

  useEffect(() => {
    if (pageFromUrl > totalPages && totalPages >= 1) {
      const next = new URLSearchParams(searchParams);
      if (totalPages <= 1) next.delete('page');
      else next.set('page', String(totalPages));
      setSearchParams(next, { replace: true });
    }
  }, [pageFromUrl, totalPages, searchParams, setSearchParams]);

  function resetPageParam() {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('page');
        return next;
      },
      { replace: true }
    );
  }

  function goToPage(nextPage: number) {
    const p = Math.min(Math.max(1, nextPage), totalPages);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p <= 1) next.delete('page');
        else next.set('page', String(p));
        return next;
      },
      { replace: true }
    );
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="pt-24">
      <SEO
        title={t('blog.seo.title')}
        description={t('blog.seo.description')}
        jsonLd={breadcrumbJsonLd([
          { name: t('common.breadcrumb.home'), url: 'https://geofleet.ai' },
          { name: t('common.breadcrumb.blog'), url: 'https://geofleet.ai/blog' },
        ])}
      />

      {/* Hero + filters */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 via-blue-50/40 to-white pb-8 lg:pb-10">
        <div
          className="pointer-events-none absolute right-0 top-0 h-[min(420px,80vw)] w-[min(520px,90vw)] translate-x-1/4 -translate-y-1/3 rounded-full bg-blue-500/[0.08] blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute bottom-0 left-0 h-[min(320px,60vw)] w-[min(400px,80vw)] -translate-x-1/3 translate-y-1/4 rounded-full bg-violet-500/[0.05] blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute left-1/2 top-1/2 h-48 w-72 -translate-x-1/2 -translate-y-1/2 rounded-full bg-emerald-500/[0.04] blur-3xl"
          aria-hidden
        />

        <div className="relative z-10 mx-auto max-w-7xl px-6 pt-10 lg:pt-14">
          <div className="mx-auto mb-8 max-w-3xl text-center lg:mb-10">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-100/90 bg-white/90 px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm backdrop-blur-sm">
              <i className="ri-article-line text-base" aria-hidden />
              <span>{t('blog.badge')}</span>
            </div>
            <h1 className="text-balance text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-[2.75rem] lg:leading-[1.12]">
              {t('blog.hero.title')}{' '}
              <span className="bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent">
                {t('blog.hero.titleHighlight')}
              </span>
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-gray-600 sm:text-lg">
              {t('blog.hero.description')}
            </p>
          </div>

          <div className="mx-auto max-w-5xl rounded-2xl border border-white/80 bg-white/80 p-4 shadow-lg shadow-blue-100/40 ring-1 ring-gray-200/60 backdrop-blur-md sm:p-5">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-stretch">
              <div className="relative sm:w-[32%] sm:max-w-[220px] sm:flex-shrink-0">
                <label htmlFor="blog-category" className="sr-only">
                  {t('blog.filters.categoryLabel')}
                </label>
                <select
                  id="blog-category"
                  value={selectedCategory}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value);
                    resetPageParam();
                  }}
                  className="w-full cursor-pointer appearance-none rounded-xl border border-gray-200/90 bg-white py-3 pl-4 pr-10 text-left text-sm font-medium text-gray-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                >
                  <option value="all">{t('blog.filters.allCategories')}</option>
                  {uniqueCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
                <i
                  className="ri-arrow-down-s-line pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-lg text-blue-600"
                  aria-hidden
                />
              </div>
              <div className="relative min-w-0 flex-1">
                <label htmlFor="blog-search" className="sr-only">
                  {t('blog.filters.searchLabel')}
                </label>
                <i
                  className="ri-search-line pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-lg text-blue-600"
                  aria-hidden
                />
                <input
                  id="blog-search"
                  type="search"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    resetPageParam();
                  }}
                  placeholder={t('blog.filters.searchPlaceholder')}
                  autoComplete="off"
                  className="w-full rounded-xl border border-gray-200/90 bg-white py-3 pl-11 pr-4 text-sm text-gray-900 shadow-sm placeholder:text-gray-500 transition-colors focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>

            <div className="mt-4 flex gap-2 overflow-x-auto pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <button
                type="button"
                onClick={() => {
                  setSelectedCategory('all');
                  resetPageParam();
                }}
                className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
                  selectedCategory === 'all'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'border border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:text-blue-700'
                }`}
              >
                {t('blog.filters.all')}
              </button>
              {uniqueCategories.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  onClick={() => {
                    setSelectedCategory(cat);
                    resetPageParam();
                  }}
                  className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition-all ${
                    selectedCategory === cat
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'border border-gray-200 bg-white text-gray-600 hover:border-blue-200 hover:text-blue-700'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured latest post */}
      {featuredPost && (
        <section className="relative z-10 -mt-2 pb-6 lg:pb-8">
          <div className="mx-auto max-w-7xl px-6">
            <div className="mb-4 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100">
                <i className="ri-star-fill text-sm text-amber-600" aria-hidden />
              </div>
              <span className="text-sm font-bold uppercase tracking-wider text-gray-900">{t('blog.featured.label')}</span>
            </div>
            <Link
              to={`/blog/${featuredPost.slug}`}
              state={{ blogListSearch: searchParams.toString() }}
              className="group grid grid-cols-1 items-stretch overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-xl shadow-gray-200/60 ring-1 ring-black/[0.03] transition-all duration-300 hover:border-blue-200/80 hover:shadow-2xl hover:shadow-blue-100/50 lg:grid-cols-5"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-gray-900 lg:col-span-3 lg:aspect-auto lg:min-h-[320px]">
                {featuredPost.image ? (
                  <>
                    <img
                      src={featuredPost.image}
                      alt=""
                      className={`h-full w-full transition-transform duration-500 group-hover:scale-[1.03] ${
                        featuredPost.image.endsWith('.svg') || featuredPost.heroImageFit === 'contain'
                          ? 'object-contain object-center bg-white'
                          : 'object-cover'
                      }`}
                    />
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-gray-900/50 via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-white/10" />
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-600 to-blue-800">
                    <i className="ri-article-line text-6xl text-white/40" />
                  </div>
                )}
              </div>
              <div className="flex min-w-0 flex-col justify-center p-6 sm:p-8 lg:col-span-2 lg:p-10">
                <span
                  className={`mb-4 inline-flex self-start text-xs font-semibold px-2.5 py-1 rounded-md ${categoryBadgeClass(featuredPost.category)}`}
                >
                  {featuredPost.category}
                </span>
                <h2 className="mb-3 text-2xl font-bold leading-tight text-gray-900 transition-colors group-hover:text-blue-600 lg:text-[1.65rem]">
                  {featuredPost.title}
                </h2>
                <p className="mb-5 line-clamp-3 text-base leading-relaxed text-gray-600">{featuredPost.excerpt}</p>
                <div className="mb-5 flex flex-wrap items-center gap-3 text-xs text-gray-500">
                  {featuredPost.author && (
                    <>
                      <span className="inline-flex items-center gap-1.5">
                        <i className="ri-user-line text-sm text-gray-400" aria-hidden />
                        {featuredPost.author}
                      </span>
                      <span aria-hidden>·</span>
                    </>
                  )}
                  <span className="inline-flex items-center gap-1.5">
                    <i className="ri-calendar-line text-sm text-gray-400" aria-hidden />
                    {formatDate(featuredPost.date, i18n.language)}
                  </span>
                  {featuredPost.readTime && (
                    <>
                      <span aria-hidden>·</span>
                      <span className="inline-flex items-center gap-1.5">
                        <i className="ri-time-line text-sm text-gray-400" aria-hidden />
                        {featuredPost.readTime}
                      </span>
                    </>
                  )}
                </div>
                <span className="inline-flex items-center gap-2 self-start rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all group-hover:bg-blue-700 group-hover:gap-3">
                  {t('blog.featured.readCta')} <i className="ri-arrow-right-line" aria-hidden />
                </span>
              </div>
            </Link>
          </div>
        </section>
      )}

      <section className="border-t border-gray-100 bg-slate-50/70 py-12 lg:py-16">
        <div className="mx-auto max-w-7xl px-6">
          <div className="mb-10 flex items-end justify-between gap-4 lg:mb-12">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-100">
                  <i className="ri-newspaper-line text-lg text-blue-600" aria-hidden />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {isDefaultView ? t('blog.list.latestTitle') : t('blog.list.title')}
                </h2>
              </div>
              <p className="text-sm text-gray-500">
                {selectedCategory !== 'all'
                  ? t('blog.list.showingInCategory', {
                      count: filteredPosts.length,
                      category: selectedCategory,
                    })
                  : t('blog.list.defaultSubtitle')}
              </p>
            </div>
            <span className="shrink-0 rounded-full border border-gray-200 bg-white px-3 py-1 text-sm font-medium text-gray-600 shadow-sm">
              {t('blog.list.article', { count: filteredPosts.length })}
            </span>
          </div>

          {filteredPosts.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white py-16 px-6 text-center shadow-sm">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100">
                <i className="ri-search-line text-2xl text-gray-400" aria-hidden />
              </div>
              <p className="mb-1 text-base font-medium text-gray-900">{t('blog.empty.title')}</p>
              <p className="text-sm text-gray-600">{t('blog.empty.hint')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 sm:gap-7 lg:grid-cols-3 lg:gap-8">
              {gridPosts.map((post) => (
                <Link
                  key={post.slug}
                  to={`/blog/${post.slug}`}
                  state={{ blogListSearch: searchParams.toString() }}
                  className="group flex flex-col overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-blue-200/80 hover:shadow-xl hover:shadow-blue-100/40"
                >
                  <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                    {post.image ? (
                      <>
                        <img
                          src={post.image}
                          alt=""
                          className={`h-full w-full transition-transform duration-500 group-hover:scale-[1.04] ${
                            post.image.endsWith('.svg') || post.heroImageFit === 'contain'
                              ? 'object-contain object-center bg-white'
                              : 'object-cover'
                          }`}
                        />
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/20 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      </>
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-50 to-slate-100">
                        <i className="ri-article-line text-5xl text-blue-400/60 transition-colors group-hover:text-blue-500/80" />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <span
                      className={`mb-3 inline-flex self-start rounded-md px-2.5 py-1 text-xs font-semibold ${categoryBadgeClass(post.category)}`}
                    >
                      {post.category}
                    </span>
                    <h3 className="mb-2 line-clamp-3 text-base font-bold leading-snug text-gray-900 transition-colors group-hover:text-blue-600">
                      {post.title}
                    </h3>
                    <p className="mb-4 line-clamp-3 flex-1 text-sm leading-relaxed text-gray-600">{post.excerpt}</p>
                    <div className="mb-4 flex items-center gap-2 text-xs text-gray-500">
                      <span>{formatDate(post.date, i18n.language)}</span>
                      {post.readTime && (
                        <>
                          <span aria-hidden>·</span>
                          <span>{post.readTime}</span>
                        </>
                      )}
                    </div>
                    <span className="mt-auto inline-flex items-center gap-1 text-sm font-semibold text-blue-600 transition-all group-hover:gap-2">
                      {t('blog.card.readMore')} <i className="ri-arrow-right-line" aria-hidden />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-12 flex flex-wrap items-center justify-center gap-3 lg:mt-14">
              {currentPage > 1 && (
                <button
                  type="button"
                  onClick={() => goToPage(currentPage - 1)}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                >
                  <i className="ri-arrow-left-s-line text-base" aria-hidden /> {t('blog.pagination.previous')}
                </button>
              )}
              <span className="rounded-full bg-white px-4 py-2 text-sm font-medium text-gray-600 shadow-sm ring-1 ring-gray-200">
                {t('blog.pagination.pageOf', { current: currentPage, total: totalPages })}
              </span>
              {currentPage < totalPages && (
                <button
                  type="button"
                  onClick={() => goToPage(currentPage + 1)}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-5 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700"
                >
                  {t('blog.pagination.next')} <i className="ri-arrow-right-s-line text-base" aria-hidden />
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-[#1769ff]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">{t('blog.cta.title')}</h2>
          <p className="text-base text-white/80 mb-7">{t('blog.cta.subtitle')}</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href="https://app.geofleet.ai/signup"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-all shadow-md"
              style={{ color: '#1769ff' }}
            >
              {t('common.ctaStartTrial')}
            </a>
            <a
              href="https://meetings.hubspot.com/aditya145"
              target="_blank"
              rel="noopener noreferrer"
              className="border-2 border-white/80 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-white/10 transition-all"
            >
              {t('common.ctaBookDemo')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
